import { useState } from "react";
import {
  CommonCodeGroupStatus,
  GetCommonCodeGroupsAsAdminDocument,
  GetCommonCodesAsAdminDocument,
  useCreateCommonCodeGroupAsAdminMutation,
  useGetCommonCodeGroupAsAdminQuery,
  useUpdateCommonCodeGroupAsAdminMutation,
} from "../../../lib/apollo/graphql/generated";
import Modal, { ModalProps } from "../../common/components/Modal";
import LabeledInput from "../../common/components/LabeledInput";
import SelectBox from "../../common/components/SelectBox";
import BasicButton from "../../common/components/BasicButton";
import Loader from "../../common/components/Loader";
import { FETCH_SIZE } from "../../../utilities/constants";

type FormValues = {
  id: string;
  groupCode: string;
  name: string;
  status: CommonCodeGroupStatus;
};

interface UpdateCodeGroupModalProps extends ModalProps {
  groupId: string | null;
}

export default function UpdateCodeGroupModal({
  groupId,
  open,
  onClose,
}: UpdateCodeGroupModalProps) {
  const [formValues, setFormValues] = useState<FormValues>({
    id: "",
    groupCode: "",
    name: "",
    status: CommonCodeGroupStatus.Pending,
  });

  const onChange = <K extends keyof FormValues>(
    key: K,
    value: FormValues[K]
  ) => {
    setFormValues((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const isCreate = !groupId;

  const { loading: initialDataLoading } = useGetCommonCodeGroupAsAdminQuery({
    skip: !groupId,
    variables: {
      groupId: groupId!,
    },
    onCompleted: (res) => {
      const group = res.getCommonCodeGroupAsAdmin;

      setFormValues({
        id: group.id,
        name: group.name,
        status: group.status,
        groupCode: group.groupCode,
      });
    },
  });

  const [updateCodeGroup, { loading: updateLoading }] =
    useUpdateCommonCodeGroupAsAdminMutation({
      onCompleted: () => {
        alert("공통코드를 수정했습니다.");
        onClose();
      },
      refetchQueries: [
        {
          query: GetCommonCodeGroupsAsAdminDocument,
          variables: {
            first: FETCH_SIZE,
          },
        },
        {
          query: GetCommonCodesAsAdminDocument,
          variables: {
            first: FETCH_SIZE,
            commonCodeGroupId: formValues.id,
          },
        },
      ],
    });

  const [createCodeGroup, { loading: createLoading }] =
    useCreateCommonCodeGroupAsAdminMutation({
      onCompleted: () => {
        alert("공통코드가 등록되었습니다");
        onClose();
      },
      refetchQueries: [
        {
          query: GetCommonCodeGroupsAsAdminDocument,
          variables: {
            first: FETCH_SIZE,
          },
        },
      ],
    });

  const isSubmitDisabled =
    !formValues.name ||
    !formValues.groupCode ||
    updateLoading ||
    createLoading ||
    initialDataLoading;

  const onSubmit = () => {
    if (isSubmitDisabled) {
      return;
    }

    if (isCreate) {
      createCodeGroup({
        variables: {
          createCommonCodeGroupInput: {
            groupCode: formValues.groupCode,
            name: formValues.name,
            status: formValues.status,
          },
        },
      });
    } else {
      updateCodeGroup({
        variables: {
          updateCommonCodeGroupInput: {
            ...formValues,
          },
        },
      });
    }
  };

  if (initialDataLoading) {
    return (
      <Modal
        title={`공통코드 그룹 ${isCreate ? "등록" : "수정"}`}
        open={open}
        onClose={onClose}
      >
        <Loader />
      </Modal>
    );
  }

  return (
    <Modal
      title={`공통코드 그룹 ${isCreate ? "등록" : "수정"}`}
      open={open}
      onClose={onClose}
    >
      <div className="flex h-[90%] w-full flex-col gap-5">
        <LabeledInput
          label="그룹명"
          value={formValues.name?.toString()}
          onChange={(event) => onChange("name", event.target.value.trim())}
        />
        <LabeledInput
          label="그룹코드"
          value={formValues.groupCode?.toString()}
          onChange={(event) => onChange("groupCode", event.target.value.trim())}
        />
        <SelectBox
          label="상태"
          value={formValues.status as CommonCodeGroupStatus}
          onSelect={(value) => onChange("status", value)}
          options={[
            { label: "활성", value: CommonCodeGroupStatus.Active },
            { label: "대기", value: CommonCodeGroupStatus.Pending },
            { label: "비활성", value: CommonCodeGroupStatus.Inactive },
          ]}
          className="h-[50px]"
        />
        <BasicButton
          text={isCreate ? "등록" : "수정"}
          className="mt-auto w-1/2 self-center"
          loading={isCreate ? createLoading : updateLoading}
          disabled={isSubmitDisabled}
          onClick={onSubmit}
        />
      </div>
    </Modal>
  );
}
